import { useState } from 'react';
import { getPortalTokenFor } from '@/lib/api/dev';
import { useWindowManager } from '@/lib/context/WindowManagerContext';

// Admin-only "view this user's portal as they see it".
// CR #123: opened in-app instead of a new tab. CR #127: uses the SAME window
// system as every other pop-out (draggable, minimizable to the taskbar) rather
// than a bespoke overlay. The portal route is rendered by the window's own
// router — no iframe — with the target user's tokens swapped in for the
// duration (admin tokens stashed + restored, with adminAccessToken backups so
// a mid-preview refresh self-heals; see AuthContext restoreSession).

interface PortalViewButtonProps {
  type: 'client' | 'employee';
  recordId: number;
  className?: string;
  onError?: (message: string) => void;
}

export function PortalViewButton({ type, recordId, className, onError }: PortalViewButtonProps) {
  const [busy, setBusy] = useState(false);
  const { openWindow } = useWindowManager();

  const open = async () => {
    setBusy(true);
    try {
      const data = await getPortalTokenFor(type, recordId);
      const role = data.role === 'employee' ? 'employee' : 'client';
      const username = (data as { username?: string }).username;
      // Tokens ride in the iframe URL hash and land in that realm's in-memory
      // store (see main.tsx + tokenStorage). The admin's own session is never
      // written to, so the app underneath keeps working while you preview.
      const hash = `#at=${encodeURIComponent(data.token)}&rt=${encodeURIComponent(data.refreshToken)}&embed=1`;
      openWindow({
        route: `/portal/${role}${hash}`,
        title: `Portal preview — ${username || (type === 'client' ? 'client' : 'employee')}`,
        icon: 'visibility',
        iframe: true,
      });
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Could not open this portal. The user may not have a portal account yet — send an invite first.';
      onError ? onError(msg) : alert(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={open}
      disabled={busy}
      title="Preview this user's portal in a window, exactly as they see it"
      className={className ?? 'flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-white text-gray-700 text-sm font-semibold border border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-60'}
    >
      <span className={`material-symbols-outlined text-base ${busy ? 'animate-spin' : ''}`}>
        {busy ? 'progress_activity' : 'preview'}
      </span>
      <span className="hidden sm:inline">{busy ? 'Opening…' : 'View Portal'}</span>
    </button>
  );
}
